import { Star } from "lucide-react";
import { Product } from "@/types/product";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import ProductModal from "./ProductModal";

interface ProductCardProps {
  product: Product;
}

const ProductCard = ({ product }: ProductCardProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const renderStars = (rating: number = 0) => {
    const roundedRating = product.rating_amount > 0 ? Math.round(rating) : 0;
    return Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${
          index < roundedRating
            ? "text-yellow-400 fill-yellow-400"
            : "text-gray-300"
        }`}
      />
    ));
  };

  // Add fallback values for potentially undefined properties
  const price = product?.price ?? 0;
  const rating = product?.average_rating ?? 0;
  const ratingAmount = product?.rating_amount ?? 0;
  const title = product?.title ?? "Untitled Product";
  const imageUrl = product?.image_url ?? "/placeholder.svg";
  const ean = product?.ean ?? "N/A";
  const slug = product?.slug ?? "N/A";

  return (
    <>
      <Card 
        className="overflow-hidden transition-all duration-300 hover:shadow-lg cursor-pointer"
        onClick={() => setIsModalOpen(true)}
      >
        <div className="aspect-square overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
          />
        </div>
        <CardContent className="p-4">
          <h3 className="font-semibold text-lg mb-1 line-clamp-2">{title}</h3>
          <p className="text-sm text-gray-500 mb-2">{slug}</p>
          <div className="flex items-center gap-1 mb-2">
            {renderStars(rating)}
            <span className="text-sm text-gray-600 ml-1">
              ({rating.toFixed(1)} - {ratingAmount} reviews)
            </span>
          </div>
          <p className="text-2xl font-bold text-primary mb-2">
            €{price.toFixed(2)}
          </p>
          <div className="text-sm text-gray-500">
            <p>EAN: {ean}</p>
          </div>
        </CardContent>
      </Card>

      <ProductModal 
        product={product}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default ProductCard;